import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import { IWorkflowEngineStatus, WorkflowEngineService } from "../../../../../services/workflow-engine-service";
import { connectWorkflowEngineStatusStream } from "../../../../../services/workflow-engine-status-stream";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaPopover from "../../../../ui-kit/kuika-popover/kuika-popover";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import { toast } from "../../../../ui-kit/kuika-toast";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import { DownloadWorkflowEngineModal } from "./download-workflow-engine-modal";
import "./workflow-engine-status.scss";

// Safety cap on the busy state: after an action we wait for the stream to push the real state (the engine
// flips when it actually answers ping), but clear it regardless after this long so a missed/dropped event
// can't leave the control stuck mid-transition.
//
// Sized for a k8s COLD start, not a warm ping. Starting a scaled-to-zero engine schedules a pod, runs the
// `fetch-artifact` init container (pulls the runtime tar + appsettings from GCS and extracts them), then
// boots the engine — the pod's own startupProbe allows ~120s for this (periodSeconds 3 × failureThreshold
// 40 in RenderWfeManifest). At the previous 15s the UI abandoned "Starting…" and flipped back to Stopped
// with a "not confirmed" warning while the pod was still legitimately initialising, which read as a failed
// start. The stream remains the real signal (it flips the badge the moment the engine answers ping); this
// timer is only the "it genuinely never came up" backstop, so it must outlast a normal cold start.
const BUSY_TIMEOUT_MS: number = 120000;

// The engine has two hard states (running/stopped) plus two transient ones while an action is in flight —
// the status stream only reports isRunning, so starting/stopping are derived from the local busy flag.
type EngineState = "running" | "stopped" | "starting" | "stopping";

type EngineAction = "start" | "stop" | "restart";

const TOAST_TITLE: string = "WF Engine";

// The lifecycle request is accepted long before the engine actually flips (k8s: the pod may still be
// pulling its runtime; systemd: the unit may still be booting), so a timed-out wait is "unconfirmed",
// not "failed" — the copy must say so, and for a start also cover the engine-never-provisioned case.
const TIMEOUT_MESSAGE: Record<EngineAction, string> = {
  start:
    "The engine hasn't confirmed it's running yet. A first start provisions the engine for this domain and can take a couple of minutes — the status will update when it responds.",
  restart:
    "The engine hasn't confirmed it's back up yet. It may still be restarting — the status will update when it responds.",
  stop: "The engine hasn't confirmed it stopped yet. The status will update when it does."
};

const STATE_LABEL: Record<EngineState, string> = {
  running: "Running",
  stopped: "Stopped",
  starting: "Starting…",
  stopping: "Stopping…"
};

// running/stopped map to green/gray; both transient states share the amber "busy" tone.
const badgeModifier = (state: EngineState): string => {
  if (state === "running") return "running";
  if (state === "stopped") return "stopped";
  return "busy";
};

// A plain colored disc (per the header redesign's Figma spec — no glyph shapes). Color alone isn't enough
// for colorblind users, but the dot never stands alone: the trigger carries the "WF Engine" label + a
// state-bearing title/aria-label, and in the menu the state text sits right next to it.
interface IStatusDotProps {
  state: EngineState;
}

const StatusDot = (props: IStatusDotProps): JSX.Element => (
  <span className={classNames("kuika-wfe-dot", `kuika-wfe-dot--${badgeModifier(props.state)}`)} aria-hidden />
);

interface IEngineGlyphProps {
  state: EngineState;
}

/**
 * The trigger's workflow-diagram pictogram (colleague's demo, tokenized): three fixed nodes
 * (diamond, two circles) joined by dashed connectors, with the BOTTOM-RIGHT node acting as the
 * status object — its SHAPE and FILL are the state (shape, not color alone, so colorblind
 * users can read it): gray square = offline, orange circle = starting/stopping, green play
 * triangle = online. The status object is larger than the fixed nodes and drawn fill-only (no
 * stroke), and the whole diagram TINTS to the status color (nodes green while running, orange
 * mid-transition, gray when stopped) so the state reads at a glance. While online the green
 * flow overlay runs clockwise along the track (kuika-wfe-flow); stopped dims the diagram.
 * Inline SVG (not a public/images asset) because the geometry is state-dependent.
 */
const EngineGlyph = (props: IEngineGlyphProps): JSX.Element => {
  const isOnline: boolean = props.state === "running";
  const isOffline: boolean = props.state === "stopped";
  const isTransient: boolean = !isOnline && !isOffline;
  return (
    <svg
      className={classNames("kuika-wfe-glyph", {
        "kuika-wfe-glyph--running": isOnline,
        "kuika-wfe-glyph--busy": isTransient,
        "kuika-wfe-glyph--stopped": isOffline
      })}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      {/* strokeWidth 3 throughout: the 48-unit viewBox renders at 24px (0.5×), so 2 painted a
          hairline 1px — too faint against the header. 3 → 1.5px keeps the pictogram legible.
          The track's dash GAP grows with the weight (round caps extend strokeWidth/2 past each
          dash end, so at width 3 the old "2 3" pattern would close up into a solid line). */}
      <path
        className="kuika-wfe-glyph__track"
        d="M18 12H30 M36 18V30 M30 36H18 M12 30V18"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray="2 4"
      />
      {/* Flow heavier than the track (4 vs 3) so the moving green dashes sit proud of the gray
          dashes underneath. Dash period stays 12 (5+7) — the keyframes' dashoffset 24 is two
          full periods, keeping the loop seamless. */}
      {isOnline && (
        <path
          className="kuika-wfe-glyph__flow"
          d="M18 12H30 M36 18V30 M30 36H18 M12 30V18"
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray="5 7"
        />
      )}
      <polygon className="kuika-wfe-glyph__node" points="12,7 17,12 12,17 7,12" strokeWidth="3" />
      <circle className="kuika-wfe-glyph__node" cx="36" cy="12" r="4.5" strokeWidth="3" />
      <circle className="kuika-wfe-glyph__node" cx="12" cy="36" r="4.5" strokeWidth="3" />
      {/* Status object — the demo's fourth node, morphing per state. Larger than the fixed
          nodes and fill-only (no stroke): it's the one shape that carries the state, so it
          gets the visual weight. */}
      {isOffline && (
        <rect
          className="kuika-wfe-glyph__status kuika-wfe-glyph__status--offline"
          x="30"
          y="30"
          width="12"
          height="12"
          rx="2"
        />
      )}
      {!isOffline && !isOnline && (
        <circle className="kuika-wfe-glyph__status kuika-wfe-glyph__status--busy" cx="36" cy="36" r="6" />
      )}
      {isOnline && (
        <polygon className="kuika-wfe-glyph__status kuika-wfe-glyph__status--online" points="31,29.5 42.5,36 31,42.5" />
      )}
    </svg>
  );
};

// Always-mounted header control: shows whether the domain's WF engine is up. An icon-only trigger (the
// workflow-diagram pictogram whose bottom-right node is the status object) opening the lifecycle card
// (Start/Restart/Stop + Download). Only visible when the project has an active workflow (otherwise
// there's no engine to run). Previews no longer auto-start the engine, so this is how the user brings it
// up. Status arrives over a single SSE stream (no per-poll HTTP request); the server pushes only on a
// transition.
const WorkflowEngineStatusBase = (props: IWithSDProps): JSX.Element | null => {
  const [status, setStatus] = useState<IWorkflowEngineStatus | null>(null);
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [downloadOpen, setDownloadOpen] = useState<boolean>(false);
  const busyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The action awaiting confirmation from the status stream; null when nothing is in flight. Used to
  // toast the outcome (confirmed flip vs. timed-out wait) — without it every path is silent except the
  // badge, which the user may not be watching.
  const pendingActionRef = useRef<EngineAction | null>(null);

  const hasActiveWorkflow: boolean = (props.sd.workFlowList ?? []).filter((w) => !w.inactived).length > 0;
  // A project with no processes of its own can still carry screens bound to ANOTHER project's
  // process (the engine is domain-wide) — its user manages the same engine, so the control shows.
  const hasWorkflowScreens: boolean = (props.sd.project?.processStepScreenIds?.length ?? 0) > 0;
  const showsEngine: boolean = hasActiveWorkflow || hasWorkflowScreens;

  const clearBusy = (): void => {
    setIsBusy(false);
    if (busyTimerRef.current) {
      clearTimeout(busyTimerRef.current);
      busyTimerRef.current = null;
    }
  };

  useEffect(() => {
    if (!showsEngine) {
      return;
    }
    const disconnect: () => void = connectWorkflowEngineStatusStream((next: IWorkflowEngineStatus): void => {
      setStatus(next);
      // A pushed status is the truth after a start/stop/restart — clear the busy flag and its safety
      // timer, and confirm the in-flight action's outcome to the user.
      const pending: EngineAction | null = pendingActionRef.current;
      if (pending) {
        pendingActionRef.current = null;
        const expectedRunning: boolean = pending !== "stop";
        if (next.isRunning === expectedRunning) {
          toast.success(next.isRunning ? "Workflow engine is running." : "Workflow engine stopped.", TOAST_TITLE);
        } else {
          toast.warning(
            next.isRunning
              ? "The engine reported running while a stop was in flight — it may have been woken again."
              : "The engine reported stopped while a start was in flight. Check the engine logs if it doesn't come up.",
            TOAST_TITLE
          );
        }
      }
      clearBusy();
    });
    return () => {
      disconnect();
      pendingActionRef.current = null;
      if (busyTimerRef.current) {
        clearTimeout(busyTimerRef.current);
        busyTimerRef.current = null;
      }
    };
  }, [showsEngine]);

  if (!showsEngine) {
    return null;
  }

  const isRunning: boolean = status?.isRunning ?? false;

  const engineState: EngineState = (() => {
    if (isBusy && !isRunning) return "starting";
    if (isBusy && isRunning) return "stopping";
    return isRunning ? "running" : "stopped";
  })();

  const runAction = async (kind: EngineAction, action: () => Promise<boolean>): Promise<void> => {
    setIsBusy(true);
    pendingActionRef.current = kind;
    if (busyTimerRef.current) {
      clearTimeout(busyTimerRef.current);
    }
    // The stream delivers the real up/down state once the engine actually flips; the timer only guards
    // against a flip that never comes — and must SAY so instead of silently snapping the badge back.
    busyTimerRef.current = setTimeout(() => {
      setIsBusy(false);
      if (pendingActionRef.current === kind) {
        pendingActionRef.current = null;
        toast.warning(TIMEOUT_MESSAGE[kind], TOAST_TITLE);
      }
    }, BUSY_TIMEOUT_MS);
    const accepted: boolean = await action();
    if (!accepted) {
      // The request itself failed — exceptionHandler already toasted the server's error message; don't
      // leave the badge spinning toward a confirmation that can never arrive.
      pendingActionRef.current = null;
      clearBusy();
    }
  };

  // The port is an implementation detail a low-code audience has no use for on the primary
  // line; it stays reachable for the advanced user inside the ⓘ tooltip below.
  const infoText: string =
    "Runs your app's Process Automation. Preview and managed hosting start it for you; download it to deploy elsewhere." +
    (status?.port ? ` Engine port: ${status.port}.` : "");

  // The popover card: a named action sheet. The trigger is icon-only, so the header NAMES the
  // tool ("WF Engine") with the live status beside it, and a one-line explainer follows — a user
  // who doesn't know the pictogram learns what they opened before reading verbs. The lifecycle
  // verbs are full-width outline buttons with a leading glyph and the tool's name in the label,
  // each colored by intent: Start = green (brings the engine UP — mirrors the running state's
  // color), Restart/Download = blue accent, Stop = red danger. Neutral gray is reserved for the
  // real disabled state — it used to be the rest state too, and read as disabled.
  // Buttons disable while a transition is in flight — the status line ("Starting…") and toasts
  // carry the progress cue. The full ⓘ explainer survives as the status line's hover title.
  const isStopped: boolean = engineState === "stopped";
  const cardContent: JSX.Element = (
    <div className="kuika-wfe-card">
      <div className="kuika-wfe-card__header">
        <span className="kuika-wfe-card__title">WF Engine</span>
        <div className="kuika-wfe-card__status" data-testid="workflowEngineMenuState" title={infoText}>
          {isRunning ? (
            <span className="kuika-wfe-card__disc" aria-hidden />
          ) : (
            <>
              {/* Two-tone glyph (dark-6 disc + dark-3 square) — original colors, the mask
                  pipeline would flatten it to one tone. Card size per design: 12px. */}
              {isStopped && <KuikaSvg name="wf-engine-stopped" useOriginalColors width={12} height={12} />}
              {!isStopped && <StatusDot state={engineState} />}
            </>
          )}
          <span className={`kuika-wfe-card__state${isRunning ? " kuika-wfe-card__state--running" : ""}`}>
            {STATE_LABEL[engineState]}
          </span>
        </div>
      </div>
      {/* One visible line of purpose; the header title above carries the full explainer. While the
          engine runs, the useful destination is its ADMIN CONSOLE (the SPA the engine serves at its
          root — instances, logs), so a direct link replaces the old raw port readout. */}
      <p className="kuika-wfe-card__info">Runs your app&apos;s Process Automation.</p>
      {isRunning && status?.adminUrl && (
        <a
          className="kuika-wfe-card__console"
          data-testid="workflowEngineAdminConsoleLink"
          href={status.adminUrl}
          target="_blank"
          rel="noreferrer"
        >
          <KuikaSvg name="external-link" width={14} height={14} color="var(--blue-5)" />
          Open Admin Console
        </a>
      )}
      {isRunning ? (
        <>
          <KuikaButton
            data-testid="workflowEngineRestartButton"
            type="secondary"
            color="neutral"
            size="sm"
            className="kuika-wfe-card__btn kuika-wfe-card__btn--accent"
            disabled={isBusy}
            onClick={() => void runAction("restart", WorkflowEngineService.Restart)}
          >
            <KuikaSvg name="refresh" width={14} height={14} color="var(--blue-5)" />
            Restart WF Engine
          </KuikaButton>
          <KuikaButton
            data-testid="workflowEngineStopButton"
            type="secondary"
            color="neutral"
            size="sm"
            className="kuika-wfe-card__btn kuika-wfe-card__btn--danger"
            disabled={isBusy}
            onClick={() => void runAction("stop", WorkflowEngineService.Stop)}
          >
            <KuikaSvg name="player-stop" width={14} height={14} color="var(--red-6)" />
            Stop WF Engine
          </KuikaButton>
        </>
      ) : (
        <KuikaButton
          data-testid="workflowEngineStartButton"
          type="secondary"
          color="neutral"
          size="sm"
          className="kuika-wfe-card__btn kuika-wfe-card__btn--success"
          disabled={isBusy}
          loading={engineState === "starting"}
          // Carries the project id so start can SELF-PROVISION on a never-previewed domain.
          onClick={() => void runAction("start", () => WorkflowEngineService.Start(props.sd.project?.id))}
        >
          <KuikaSvg name="player-play" width={14} height={14} color="var(--green-6)" />
          Start WF Engine
        </KuikaButton>
      )}
      {/* Deployment, not lifecycle — always available (moved here from the trigger's split),
          set apart from the lifecycle verbs by its own hairline section. */}
      <div className="kuika-wfe-card__deploy">
        <KuikaTooltip content="Download the engine to deploy elsewhere">
          <KuikaButton
            data-testid="workflowEngineDownloadButton"
            type="secondary"
            color="neutral"
            size="sm"
            className="kuika-wfe-card__btn kuika-wfe-card__btn--accent"
            onClick={() => setDownloadOpen(true)}
          >
            <KuikaSvg name="download" width={14} height={14} color="var(--blue-5)" />
            Download WF Engine
          </KuikaButton>
        </KuikaTooltip>
      </div>
    </div>
  );

  return (
    <>
      {/* Icon-only trigger: the workflow-diagram pictogram whose BOTTOM-RIGHT node doubles as
          the status object (gray square = offline, orange circle = starting/stopping, green
          play triangle = online — shape + fill carry the state, no separate dot). The download
          split is gone; Download lives inside the card. Stopped greys the frame like before;
          hover fills --blue-05; open turns the frame --blue-5. */}
      <KuikaPopover
        position="bottom"
        align="end"
        sideOffset={2}
        hideCloseIcon
        className="kuika-wfe-menu"
        content={cardContent}
        visible={menuOpen}
        onVisibleChange={setMenuOpen}
      >
        <KuikaButton
          data-testid="workflowEngineButton"
          type="secondary"
          color="neutral"
          size="sm"
          isIcon
          active={menuOpen}
          className={classNames("kuika-wfe-trigger", {
            "kuika-wfe-trigger--running": engineState === "running",
            "kuika-wfe-trigger--busy": engineState === "starting" || engineState === "stopping",
            "kuika-wfe-trigger--stopped": isStopped,
            "kuika-wfe-trigger--open": menuOpen
          })}
          aria-label={`WF Engine — ${STATE_LABEL[engineState]}`}
        >
          {/* Icon-only control — the title is the hover hint carrying name + state. */}
          <span className="kuika-wfe-trigger__glyph" title={`WF Engine — ${STATE_LABEL[engineState]}`}>
            <EngineGlyph state={engineState} />
          </span>
        </KuikaButton>
      </KuikaPopover>
      <DownloadWorkflowEngineModal open={downloadOpen} onClose={() => setDownloadOpen(false)} />
    </>
  );
};

const workflowEngineStatus = withSD<object>(WorkflowEngineStatusBase);
export { workflowEngineStatus as WorkflowEngineStatus };
